import {
    useAPIQuery,
    useGlobalAPIQueryContext,
} from "@dev2_techwalnut/tw-api-query-hook";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import secureLocalStorage from "react-secure-storage";
import Swal from "sweetalert2";

const useMachineShiftAssociation = ({ machine_id, activeAccordion }) => {
    const userID = secureLocalStorage.getItem("userID");
    const { invalidateApi } = useGlobalAPIQueryContext();
    const [currentSlot, setCurrentSlot] = useState(null);
    const [currentShift, setCurrentShift] = useState(null);
    const [currentDate, setCurrentDate] = useState(null);
    const [shifts, setShifts] = useState([]);
    const [tempShifts, setTempShifts] = useState([]);

    const {
        useFetch: useFetchMachineInfo,
        useCreate: useCreateMachineShiftAssociation,
    } = useAPIQuery({
        key: `machine-shift-info-${machine_id}`,
        endpoint: "/api/machine-shift/",
    });

    const {
        data: machineData,
        isLoading: loadingMachineInfoData,
        isError: errorLoadingMachineInfo,
        isFetching: fetchingMachineData,
    } = useFetchMachineInfo({
        params: {
            machine_id: machine_id,
            date: moment(currentDate ?? new Date()).format("yyyy-MM"),
        },
        staleTime: 500000,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        retry: 3,
        keepPlaceholder: true,
        enabled: machine_id === activeAccordion,
    });

    const {
        mutate: createMachineShiftAssocaition,
        isPending: pendingCreateMachineShiftAssocaition,
    } = useCreateMachineShiftAssociation({
        retry: 2,
        onSuccess: (data) => {
            Swal.fire({
                title: "Success!",
                text: data?.message,
                icon: "success",
            }).then((result) => {
                invalidateApi({ key: `machine-info-${machine_id}` });
                setTempShifts(shifts);
            });
        },
        onError: (error) => {
            Swal.fire({
                title: "Error!",
                text: error?.message,
                icon: "error",
            }).then((result) => {
                setShifts(tempShifts);
            });
        },
    });

    const handleSelectEvent = (e) => {
        console.log(e);
    };
    const handleSelectSlot = (e) => {
        if (
            new Date(e.slots[0]).setHours(0, 0, 0, 0) <
            new Date().setHours(0, 0, 0, 0)
        )
            return;

        const shift = shifts?.find(
            (s) => s?.date === moment(e.slots[0]).format("YYYY-MM-DD")
        );

        if (shift) {
            setCurrentShift(shift);
        } else {
            setCurrentShift(null);
        }

        setCurrentSlot(e.slots[0]);
        // open modal
        const button = document.createElement("button");
        button.className = "d-none";
        button.id = "open-modal";
        button.setAttribute("data-bs-toggle", "modal");
        button.setAttribute(
            "data-bs-target",
            `#machine-shift-association-modal-${machine_id}`
        );
        document.body.appendChild(button);
        button.click();
        button.remove();
    };

    const handleUpdateShifts = (type, range, comments) => {
        const start = moment(range[0]);
        const end = range[1] ? moment(range[1]) : start;
        const curr = start.clone();

        const updatedShifts = [...shifts];
        const changedAssociations = [];

        while (curr.isSameOrBefore(end, "day")) {
            const formattedDate = curr.format("YYYY-MM-DD");

            const existingIndex = updatedShifts.findIndex(
                (s) => s.date === formattedDate
            );

            if (existingIndex !== -1) {
                updatedShifts[existingIndex] = {
                    ...updatedShifts[existingIndex],
                    type,
                    comments,
                };
                changedAssociations.push(updatedShifts[existingIndex]);
            } else {
                const newEntry = { date: formattedDate, type, comments };
                updatedShifts.push(newEntry);
                changedAssociations.push(newEntry);
            }

            curr.add(1, "day");
        }

        setShifts(updatedShifts);

        const button = document.createElement("button");
        button.className = "d-none";
        button.setAttribute(
            "data-bs-target",
            `#machine-shift-association-modal-${machine_id}`
        );
        button.setAttribute("data-bs-dismiss", "modal");
        document.body.appendChild(button);
        button.click();
        button.remove();

        createMachineShiftAssocaition({
            data: {
                machine_id: machine_id,
                dates: changedAssociations?.map((c) => c?.date),
                shift: type?.charAt(0)?.toUpperCase() + type?.slice(1),
                comments: comments || "",
                user_id: userID,
            },
        });
    };

    useEffect(() => {
        if (machineData?.results?.data) {
            const res = machineData?.results?.data;
            const mData = [
                ...res?.map((r) => ({
                    date: r?.date,
                    type: r?.shift?.toLowerCase(),
                    comments: r?.comments || "",
                })),
            ];
            setShifts(mData);
            setTempShifts(mData);
        }
    }, [machineData]);

    return {
        currentSlot,
        currentShift,
        shifts,
        setCurrentDate,
        handleSelectSlot,
        handleSelectEvent,
        handleUpdateShifts,
        loadingMachineInfoData,
        fetchingMachineData,
        errorLoadingMachineInfo,
        pendingCreateMachineShiftAssocaition,
    };
};

export default useMachineShiftAssociation;
